import { useTheme } from "styled-components";
import Modal, { ModalCard } from "../../../components/Modal/Modal";
import Icon from "../../../components/Icon/Icon";
import { FormikProvider, useFormik } from "formik";
import Button from "../../../components/Button/Button";
import * as Yup from "yup";
import { useAlert } from "../../../components/Alert/Alerts";
import { useCallback, useState } from "react";
import { updateEntryProperty } from "../../Entries/manageEntry";
import DropdownField from "../../../components/FormFields/DropdownField";
import { DropdownOptions } from "../../../hooks/useAwardableAwards";
import {
	ReviewStatus,
	ReviewStatusOptions,
} from "../../Checkout/OrderInterfaces";

const AssignmentModal = (props: AssignmentModalProps) => {
	const theme = useTheme();
	const { addNewAlert } = useAlert();
	const [isLoading, setIsLoading] = useState(false);

	const formikProps = useFormik({
		initialValues: {
			selectedUserId: "",
			reviewStatusId: "",
		},
		onSubmit: async (values) => {},
		validationSchema: Yup.object({}),
	});

	const handleClose = () => {
		setIsLoading(false);
		formikProps.resetForm();
		props.onHide();
	};

	const assignReviewer = useCallback(
		(entryId: number, userId: string): Promise<number> => {
			return new Promise((resolve, reject) => {
				updateEntryProperty({
					id: entryId,
					propertyName: "reviewerId",
					propertyValue: userId === "Unassigned" ? null : userId,
				})
					.then((res) => {
						resolve(entryId);
					})
					.catch((e) =>
						reject(`Error Assigning Reviewer to Entry: ${e.message}`)
					);
			});
		},
		[]
	);

	const handleAssignReviewer = () => {
		setIsLoading(true);
		Promise.all(
			props.selectedEntriesArr.map((entryId) =>
				assignReviewer(Number(entryId), formikProps.values.selectedUserId)
			)
		)
			.then((res) => {
				handleClose();
				props.onSucess();
			})
			.catch((err) => {
				addNewAlert({ type: "error", message: err });
			});
	};

	const setReviewStatus = useCallback(
		(entryId: number, reviewStatusId: number | string): Promise<number> => {
			return new Promise((resolve, reject) => {
				updateEntryProperty({
					id: entryId,
					propertyName: "reviewStatusId",
					propertyValue: reviewStatusId === "" ? null : reviewStatusId,
				})
					.then((res) => {
						resolve(entryId);
					})
					.catch((e) =>
						reject(`Error Setting Review Status for Entry: ${e.message}`)
					);
			});
		},
		[]
	);

	const handleUpdateReviewStatus = () => {
		setIsLoading(true);
		Promise.all(
			props.selectedEntriesArr.map((entryId) =>
				setReviewStatus(Number(entryId), formikProps.values.reviewStatusId)
			)
		)
			.then((res) => {
				handleClose();
				props.onSucess();
			})
			.catch((err) => {
				addNewAlert({ type: "error", message: err });
			});
	};

	const handleSave = () => {
		if (props.modalType === ModalType.AssignReviewer) {
			handleAssignReviewer();
		} else {
			handleUpdateReviewStatus();
		}
	};

	return (
		<FormikProvider value={formikProps}>
			<Modal show={props.modalType !== null}>
				<ModalCard
					title={
						props.modalType === ModalType.AssignReviewer
							? "Assign Reviewer"
							: "Update Review Status"
					}
					headerIcons={
						<Icon
							icon="close"
							color={theme.colorPrimary}
							onClick={handleClose}
							width="35px"
							height="35px"
						/>
					}
					iconColor={theme.colorPrimary}
				>
					<div className="flex flex-col gap-[2rem] p-[2rem]">
						<p>
							{props.modalType === ModalType.ReviewStatus
								? "Update Review Status of Selected Entries"
								: "Assign Selected Entries to Reviewer"}
						</p>

						{props.modalType === ModalType.ReviewStatus ? (
							<>
								<p>
									NOTE: If judging has already started for these entries,
									passing them here will NOT push them into judging
									automatically. Please pass them from the Admin Entry Edit Page
									to move them into judging.
								</p>
								<DropdownField
									name="reviewStatusId"
									placeholder="Review Status"
									options={ReviewStatusOptions}
								/>
							</>
						) : (
							<DropdownField
								name="selectedUserId"
								placeholder="Reviewer"
								options={props.reviewerOptions}
							/>
						)}

						<div className="flex gap-[1rem]">
							<Button
								className="button-gold ml-auto w-[200px]"
								icon={"close"}
								iconSize="18px"
								iconColor={theme.colorPrimary}
								onClick={handleClose}
								disabled={isLoading}
							>
								Discard Changes
							</Button>
							<Button
								className="w-[200px]"
								iconSize="15px"
								icon={isLoading ? "loading" : "check"}
								iconColor={theme.colorCopyLightLight}
								onClick={handleSave}
								disabled={isLoading}
							>
								Save Changes
							</Button>
						</div>
					</div>
				</ModalCard>
			</Modal>
		</FormikProvider>
	);
};

export default AssignmentModal;

interface AssignmentModalProps {
	modalType: ModalType | null;
	onHide(): void;
	onSucess(): void;
	reviewerOptions: DropdownOptions[];
	selectedEntriesArr: string[];
}

export enum ModalType {
	ReviewStatus,
	AssignReviewer,
}
